import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LineChartOutlined,
  PlusOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { Alert, Avatar, Button, Progress, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { AdminRobot } from "@/entities/robot";
import { useRobots } from "@/entities/robot";
import { useAuth } from "@/entities/session";
import { useReorderRobots } from "@/features/manage-robot";
import { errorMessage } from "@/shared/api";
import { ROUTES, SECTIONS, sectionSurface } from "@/shared/config";
import { formatPercent } from "@/shared/lib";
import { EmptyState, Money, PageHeader, SectionCard, StatusTag } from "@/shared/ui";

export function RobotsPage() {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useRobots();
  const { isSuperadmin } = useAuth();
  const reorder = useReorderRobots();
  const rows = data ?? [];

  /** Поменять робота местами с соседом. Тело — весь каталог: ручка правит
   *  порядок целиком, потому что `sort_order` у заведённых оператором
   *  роботов нулевой и обмен двух значений ничего не двигает. */
  const move = (from: number, to: number) => {
    const ids = rows.map((robot) => robot.id);
    const [moved] = ids.splice(from, 1);
    if (moved === undefined) return;
    ids.splice(to, 0, moved);
    reorder.mutate({ ids });
  };

  const columns: ColumnsType<AdminRobot> = [
    // Стрелки, а не drag-n-drop: библиотеки dnd в проекте нет, восьми строк
    // каталога она не стоит, а кнопки доступны с клавиатуры и читаются
    // скринридером. Колонка видна только тому, кто вправе править каталог.
    ...(isSuperadmin
      ? [
          {
            title: "",
            key: "order",
            width: 88,
            align: "center" as const,
            render: (_: unknown, __: AdminRobot, index: number) => (
              <Space size={0}>
                <Button
                  type="text"
                  shape="circle"
                  aria-label={t("robots.moveUp")}
                  icon={<ArrowUpOutlined aria-hidden />}
                  disabled={index === 0}
                  onClick={() => {
                    move(index, index - 1);
                  }}
                />
                <Button
                  type="text"
                  shape="circle"
                  aria-label={t("robots.moveDown")}
                  icon={<ArrowDownOutlined aria-hidden />}
                  disabled={index === rows.length - 1}
                  onClick={() => {
                    move(index, index + 1);
                  }}
                />
              </Space>
            ),
          },
        ]
      : []),
    {
      title: t("robots.name"),
      key: "name",
      render: (_, row) => (
        <Space size={10} align="center">
          <Avatar
            size={32}
            shape="square"
            // Картинка задаётся в форме робота; пока её нет — прежняя заглушка
            // с иконкой, а не пустой квадрат.
            src={row.image_url ?? undefined}
            style={{
              background: row.accent_color ?? sectionSurface(SECTIONS.robots.color),
              color: SECTIONS.robots.color,
              borderRadius: 12,
              flex: "none",
            }}
            icon={<RobotOutlined aria-hidden />}
          />
          <Space orientation="vertical" size={0}>
            <Link to={ROUTES.robot(row.id)} style={{ fontWeight: 500 }}>
              {row.name}
            </Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.code}
            </Typography.Text>
          </Space>
        </Space>
      ),
    },
    {
      title: t("robots.minAmount"),
      key: "min_amount",
      align: "right",
      render: (_, row) => <Money value={row.min_amount} />,
    },
    {
      title: t("robots.declaredYield"),
      key: "declared_yield_12m",
      align: "right",
      width: 210,
      render: (_, row) => (
        <Typography.Text
          strong
          style={{
            color:
              Number(row.declared_yield_12m) < 0
                ? "var(--ant-color-error)"
                : "var(--ant-color-success)",
          }}
        >
          {formatPercent(row.declared_yield_12m, { sign: "always" })}
        </Typography.Text>
      ),
    },
    {
      // Диапазон и вероятность в одной колонке: это одна настройка, и читать
      // её врозь бессмысленно.
      title: t("robots.generator"),
      key: "generator",
      render: (_, row) =>
        `${formatPercent(row.daily_min_pct)} … ${formatPercent(row.daily_max_pct, {
          sign: "always",
        })} · ${formatPercent(row.positive_chance)}`,
    },
    { title: t("robots.purchases"), dataIndex: "purchases_count", align: "right", width: 120 },
    {
      // Кампания одной колонкой: заполненность без состояния читается как
      // «мало собрали», хотя предложение может быть просто ещё не начатым.
      title: t("robots.offer"),
      key: "offer",
      width: 200,
      render: (_, row) =>
        row.is_limited ? (
          <Space orientation="vertical" size={2} style={{ display: "flex" }}>
            <Progress percent={Number(row.pool_fill_pct)} size="small" />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.pool_used} / {row.pool_limit}
            </Typography.Text>
            <Tag>{t(`robots.offerStateValue.${row.offer_state ?? "active"}`)}</Tag>
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: t("common.status"),
      key: "status",
      width: 190,
      render: (_, row) => <StatusTag status={row.status} />,
    },
    {
      title: "",
      key: "analytics",
      width: 120,
      render: (_, row) => (
        <Link to={ROUTES.robotAnalytics(row.id)}>
          <Button type="link" size="small" icon={<LineChartOutlined aria-hidden />}>
            {t("robots.analytics")}
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        section="robots"
        title={t("robots.title")}
        subtitle={t("robots.subtitle")}
        extra={
          <Link to={ROUTES.robotNew}>
            <Button type="primary" icon={<PlusOutlined aria-hidden />}>
              {t("robots.create")}
            </Button>
          </Link>
        }
      />

      {isError && (
        <Alert type="error" showIcon title={errorMessage(error)} style={{ marginBottom: 16 }} />
      )}

      <SectionCard color={SECTIONS.robots.color} bodyPadding={0}>
        <Table<AdminRobot>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={isPending}
          pagination={false}
          // Ширина поднята с 900 вслед за базовым шрифтом (12 → 16px).
          scroll={{ x: 1400 }}
          locale={{
            emptyText: (
              <EmptyState
                icon={<RobotOutlined aria-hidden />}
                title={t("robots.empty")}
                hint={t("robots.emptyHint")}
              />
            ),
          }}
        />
      </SectionCard>
    </>
  );
}
